import React from "react";
import { Link } from "react-router-dom";
import { Stethoscope, ShoppingBag, Building2, GraduationCap, UtensilsCrossed, Scale, ArrowRight } from "lucide-react";
import MarketingLayout from "../../components/marketing/MarketingLayout";
import Seo, { ORG_JSON_LD } from "../../components/marketing/Seo";

const USE_CASES = [
  { slug: "healthcare",   name: "Healthcare",  Icon: Stethoscope,    color: "#06B6D4", tag: "Clinics · Dental · Hospitals", desc: "AI receptionist for patient intake, bookings and routine questions — never gives medical advice." },
  { slug: "ecommerce",    name: "eCommerce",   Icon: ShoppingBag,    color: "#F97316", tag: "Shopify · WooCommerce · Magento", desc: "Recommend products, recover carts, track orders and process returns across your storefronts." },
  { slug: "real-estate",  name: "Real Estate", Icon: Building2,      color: "#7C3AED", tag: "Agents · Brokers · Portals", desc: "Match buyers to listings, book viewings and answer area & finance questions 24/7." },
  { slug: "education",    name: "Education",   Icon: GraduationCap,  color: "#8B5CF6", tag: "Schools · Colleges · EdTech", desc: "Course advisor, admissions intake, open-day booking and scholarship matching." },
  { slug: "restaurants",  name: "Restaurants", Icon: UtensilsCrossed, color: "#F59E0B", tag: "Bookings · Menus · POS", desc: "Reservations, menu Q&A, dietary aware, pickup/delivery orders pushed into your POS." },
  { slug: "law-firms",    name: "Law Firms",   Icon: Scale,          color: "#0F766E", tag: "PI · Family · Immigration · Conveyancing", desc: "Client intake, conflict screen, paid-consult booking — without giving legal advice." },
];

const HUB_LD = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  url: "https://hellosir.ai/use-cases",
  name: "AI Agent Use Cases — HelloSir.ai",
  description:
    "Industry-specific AI agent use cases — healthcare, eCommerce, real estate, education, restaurants and law firms. Train once, deploy on chat, web voice and phone.",
  isPartOf: ORG_JSON_LD,
};

export default function UseCases() {
  return (
    <MarketingLayout>
      <Seo
        title="AI Agent Use Cases by Industry"
        description="Discover how HelloSir.ai AI agents work in your industry — healthcare, eCommerce, real estate, education, restaurants, law firms and more. Multilingual chat, web voice and AI calling agents. A Cloud Lab Technologies (UK) product."
        keywords={[
          "AI agent use cases", "AI chatbot by industry", "AI calling agent industries",
          "vertical AI agents", "AI for small business", "AI for enterprise",
          "AI for healthcare ecommerce real-estate education restaurants law",
        ]}
        path="/use-cases"
        jsonLd={HUB_LD}
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#E2E8F0] bg-gradient-to-br from-[#F0F4FF] via-white to-[#FFF1F8]">
        <div className="absolute -top-32 right-1/4 w-[480px] h-[480px] rounded-full blur-3xl opacity-40 animate-hs-blob"
             style={{ background: "radial-gradient(circle, #0044FF 0%, transparent 70%)" }} aria-hidden />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <span className="text-xs font-mono uppercase tracking-widest text-[#0044FF]">// use cases</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl lg:text-7xl mt-3 leading-[0.95] tracking-tight">
            One platform.<br />
            <span className="bg-gradient-to-r from-[#0044FF] via-[#7C3AED] to-[#EC4899] bg-clip-text text-transparent">Every industry.</span>
          </h1>
          <p className="text-lg text-[#4B5563] mt-8 max-w-3xl">
            Whether you run a single dental clinic or a global eCommerce store, HelloSir.ai gives you a multilingual text chatbot, voice chatbot and AI calling
            agent built for your exact workflow. Pick your industry to see how it fits.
          </p>
        </div>
      </section>

      {/* GRID */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {USE_CASES.map((u) => (
            <Link
              key={u.slug}
              to={`/use-cases/${u.slug}`}
              className="group relative rounded-[6px] bg-white border border-[#E2E8F0] p-7 hover:border-[#030712] hover:shadow-[0_18px_40px_-12px_rgba(2,6,23,0.18)] hover:-translate-y-1 transition-all overflow-hidden"
              data-testid={`use-case-${u.slug}`}
            >
              <div
                className="absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                style={{ background: `linear-gradient(90deg, ${u.color}, transparent)` }}
              />
              <div
                className="w-12 h-12 rounded-[6px] flex items-center justify-center"
                style={{ background: `${u.color}14`, color: u.color }}
              >
                <u.Icon className="w-6 h-6" />
              </div>
              <div className="font-display font-bold text-xl mt-6">{u.name}</div>
              <div className="text-[11px] font-mono uppercase tracking-widest mt-1" style={{ color: u.color }}>{u.tag}</div>
              <p className="text-sm text-[#4B5563] mt-4 leading-relaxed">{u.desc}</p>
              <div className="mt-6 inline-flex items-center gap-1 text-sm font-medium" style={{ color: u.color }}>
                Explore use case <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>

        <div className="max-w-6xl mx-auto px-6 lg:px-10 mt-12 text-center">
          <p className="text-sm text-[#4B5563]">
            Don't see your industry? <Link to="/contact" className="text-[#0044FF] hover:underline font-medium">Tell us about your workflow</Link> —
            our agents adapt to any vertical with structured intake.
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
}
